import { useDispatch } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../features/userSlice";
import { useState } from "react";
import { useSelector } from "react-redux";
import { ProcessLogsEndPointLogic } from "../logic/DataCollection/Monitoring/ProcessLogsEndPointLogic";
import {
  setProcessLogsList,
  setProcessLogsSummeryData,
  setIsProcessLogsLoading,
  setIsTableLoading,
} from "../features/monitoringProcessLogsSlice";

export const useMonitoringProcessLogs = () => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const fetchMonitoringProcessLogs = async (
    from_date: any,
    to_date: any,
    run_id?: any,
    previousList?: any[],
    filters?: {
      status?: string[];
      run_method?: string[];
      step?: string[];
      process_id?: string[];  // Changed from process_name to process_id
      package_name?: string[];
      brand_id?: string[];
      interface_id?: string[];
      workflow_name?: string[];
    },
    sort_field?: string,
    sort_order?: "ASC" | "DESC"
  ): Promise<number> => {
    // Only reset if it's a fresh load (no run_id and no previous list)
    if (!run_id && (!previousList || previousList.length === 0)) {
      dispatch(setProcessLogsList([]));
      dispatch(setProcessLogsSummeryData([]));
    }

    setIsLoading(true);
    if (!run_id) {
      dispatch(setIsProcessLogsLoading(true));
    }
    dispatch(setIsTableLoading(true));

    try {
      // Prepare cleaned filters (remove undefined/empty values)
      const cleanedFilters = filters 
        ? Object.fromEntries(
            Object.entries(filters).filter(
              ([_, value]) => 
                value !== undefined && 
                value !== null && 
                (Array.isArray(value) ? value.length > 0 : true)
            )
          )
        : undefined;

      const processLogsResponse =
        await ProcessLogsEndPointLogic.getAllProcessLogsList(
          oauthTokenHeader,
          from_date,
          to_date,
          dispatch,
          run_id,
          previousList || [],
          cleanedFilters,
          sort_field,
          sort_order
        );

      const newRecords = processLogsResponse?.data?.valid_records || [];
      const count = newRecords.length;

      // If we're appending data (run_id exists), manually combine the arrays
      if (run_id && previousList && newRecords.length > 0) {
        const combinedData = [...previousList, ...newRecords];
        dispatch(setProcessLogsList(combinedData));
      }

      return count;
    } catch (error) {
      const errorMessage =
        typeof error === "string"
          ? error
          : error instanceof Error
          ? error.message
          : "An unexpected error occurred.";

      dispatch(
        setDisplayErrorSnackBar({ message: errorMessage, type: "error" })
      );
      return 0;
    } finally {
      setIsLoading(false);
      dispatch(setIsProcessLogsLoading(false));
      dispatch(setIsTableLoading(false));
    }
  };

  const postArgoPOD = async (data: any) => {
    setIsLoading(true);
    try {
      const response = await ProcessLogsEndPointLogic.postArgoPODLog(
        oauthTokenHeader,
        data,
        dispatch
      );
      if (response.status !== 200) {
        throw new Error("Failed to access podlog viewer data");
      }
    } catch (e: any) {
      dispatch(
        setDisplayErrorSnackBar({
          message: e?.parsedText?.reason,
          type: "error",
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  return { fetchMonitoringProcessLogs, isLoading, postArgoPOD };
};
